/**
 * Backend Realtime API React Hook
 *
 * WebSocket 연결 및 상태 관리
 * - useRef로 콜백 최신 상태 보장 (stale closure 방지)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BackendRealtimeAPI, BackendRealtimeConfig } from '../api/backend-realtime-api';
import type { ServerMessage, ConnectionState, SearchState } from '../types/messages';
import { generateUUID } from '@/shared/utils/uuid';

export interface UseBackendRealtimeProps {
  agentId: string;
  avatarId: string;
  enableWebSearch?: boolean;
  autoConnect?: boolean;
  token?: string;
  provider?: 'openai' | 'google' | 'none';  // LLM provider 선택
  onMessage?: (message: ServerMessage) => void;
  onTextDelta?: (delta: string, fullText: string) => void;
  onSearchStart?: (query: string) => void;
  onSearchComplete?: (query: string, resultsCount: number) => void;
  onError?: (error: Error) => void;
}

export interface UseBackendRealtimeReturn {
  // 연결 상태
  isConnected: boolean;
  connectionState: ConnectionState;
  sessionId: string | null;

  // 검색 상태
  searchState: SearchState;
  searchQuery: string | null;
  isSearchEnabled: boolean;

  // 응답 텍스트
  currentResponseText: string;

  // 액션
  connect: () => Promise<void>;
  disconnect: () => void;
  sendText: (content: string) => boolean;
  sendTextWithRag: (content: string) => boolean;
  setSearchEnabled: (enabled: boolean) => void;
}

export function useBackendRealtime({
  agentId,
  avatarId,
  enableWebSearch = false,
  autoConnect = false,
  token,
  provider,
  onMessage,
  onTextDelta,
  onSearchStart,
  onSearchComplete,
  onError,
}: UseBackendRealtimeProps): UseBackendRealtimeReturn {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [isSearchEnabled, setIsSearchEnabledState] = useState(enableWebSearch);
  const [currentResponseText, setCurrentResponseText] = useState('');
  const [sessionId] = useState(() => generateUUID());

  const apiRef = useRef<BackendRealtimeAPI | null>(null);
  const mountedRef = useRef(true);

  // useRef로 콜백 최신 상태 보장 (stale closure 방지)
  const onMessageRef = useRef(onMessage);
  const onTextDeltaRef = useRef(onTextDelta);
  const onSearchStartRef = useRef(onSearchStart);
  const onSearchCompleteRef = useRef(onSearchComplete);
  const onErrorRef = useRef(onError);

  // 매 렌더링마다 ref 업데이트
  onMessageRef.current = onMessage;
  onTextDeltaRef.current = onTextDelta;
  onSearchStartRef.current = onSearchStart;
  onSearchCompleteRef.current = onSearchComplete;
  onErrorRef.current = onError;

  // API 인스턴스 생성
  useEffect(() => {
    mountedRef.current = true; // 재실행 시 리셋

    const config: BackendRealtimeConfig = {
      agentId,
      avatarId,
      sessionId,
      enableWebSearch: isSearchEnabled,
      token,
      provider,
      onMessage: (message) => {
        if (!mountedRef.current) return;
        onMessageRef.current?.(message);

        // 검색 이벤트 처리
        if (message.type === 'search.started') {
          onSearchStartRef.current?.(message.query as string);
        } else if (message.type === 'search.completed') {
          onSearchCompleteRef.current?.(message.query as string, message.results_count as number);
        }
      },
      onConnectionChange: (state) => {
        if (!mountedRef.current) return;
        setConnectionState(state);
      },
      onSearchStateChange: (state, query) => {
        if (!mountedRef.current) return;
        setSearchState(state);
        setSearchQuery(query ?? null);
      },
      onTextDelta: (delta, fullText) => {
        if (!mountedRef.current) return;
        setCurrentResponseText(fullText);
        onTextDeltaRef.current?.(delta, fullText);
      },
      onError: (error) => {
        if (!mountedRef.current) return;
        onErrorRef.current?.(error);
      },
    };

    apiRef.current = new BackendRealtimeAPI(config);

    return () => {
      mountedRef.current = false;
      apiRef.current?.disconnect();
      apiRef.current = null;
    };
  }, [agentId, avatarId, sessionId, token, provider]); // isSearchEnabled 제외: setWebSearchMode()로 런타임 변경

  // 자동 연결
  useEffect(() => {
    if (autoConnect && apiRef.current) {
      apiRef.current.connect().catch((error) => {
        console.error('[useBackendRealtime] Auto-connect failed:', error);
      });
    }
  }, [autoConnect]);

  // 연결
  const connect = useCallback(async () => {
    if (!apiRef.current) return;
    await apiRef.current.connect();
  }, []);

  // 연결 해제
  const disconnect = useCallback(() => {
    apiRef.current?.disconnect();
  }, []);

  // 텍스트 전송
  const sendText = useCallback((content: string) => {
    setCurrentResponseText('');
    return apiRef.current?.sendText(content) ?? false;
  }, []);

  // RAG 포함 텍스트 전송
  const sendTextWithRag = useCallback((content: string) => {
    setCurrentResponseText('');
    return apiRef.current?.sendTextWithRag(content) ?? false;
  }, []);

  // 검색 모드 설정
  const setSearchEnabled = useCallback((enabled: boolean) => {
    setIsSearchEnabledState(enabled);
    apiRef.current?.setWebSearchMode(enabled);
  }, []);

  return {
    isConnected: connectionState === 'connected',
    connectionState,
    sessionId,
    searchState,
    searchQuery,
    isSearchEnabled,
    currentResponseText,
    connect,
    disconnect,
    sendText,
    sendTextWithRag,
    setSearchEnabled,
  };
}
